import React, { useState, FormEvent, useEffect, useMemo } from 'react'
import { connect, useDispatch } from 'react-redux'
import { Stack, Paper, TextField, IconButton, Tooltip, CircularProgress } from '@mui/material'
import ProgressButton from '../../../Utils/ProgressButton'
import { entityTextField } from '../../../Utils/EntityField'
import { AppState } from '../../../../reducers/index'
import { upsertComplaintMessage } from '../../../../actions/complaint/complaintMessageUpsert'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { fetchComplaintMessage } from '../../../../actions/complaint/complaintMessageDetail'
import moment from 'moment'
import ComplaintMessage from '../../../../models/ComplaintMessage'
import { ComplaintMessageDetailState } from '../../../../reducers/complaint/complaintMessageDetail'
import { fetchComplaintMessages } from '../../../../actions/complaint/complaintMessage'
import { uploadComplaintMessageFile } from '../../../../actions/complaint/complaintMessageUploadFile'
import { AttachFile } from '@mui/icons-material';
import { useThemeContext } from '../../../../theme/ThemeContext'

interface Props {
  message: ComplaintMessageDetailState
}

const empty: ComplaintMessage = {
  date: moment().tz("America/Argentina/Buenos_Aires").format(),
  message: '',
  status: 'U',
  complaintId: 0,
  ownerName: '',
}

const ComplaintMessageUpsert: React.FC<Props> = ({ message }) => {
  const [changed, setChanged] = useState<boolean>(false)
  const [fetchingUploadFile, setFetchingUploadFile] = useState<boolean>(false)
  const [complaintMessageInfo, setComplaintMessageInfo] = useState<ComplaintMessage>(message.message || empty)
  const [complaintMessageId, setComplaintMessageId] = useState<number | undefined>(undefined)
  const [complaintMessageFileId, setComplaintMessageFileId] = useState<string | undefined>(undefined)
  const [readMessages, setReadMessages] = useState<boolean | undefined>(undefined)
  const [attached, setAttached] = useState<boolean | undefined>(undefined)
  const [showUploadFile, setShowUploadFile] = useState<boolean>(false)
  const fileInput = React.useRef<HTMLInputElement>(null);
  const params = useParams<{ complaintId: string, complaintMessageId: string }>()
  const dispatch = useDispatch()
  
  const { t } = useTranslation('complaint')
  const { theme } = useThemeContext()

  useEffect(() => {
    if (params.complaintMessageId) {
      dispatch(fetchComplaintMessage(params.complaintMessageId))
    }
  }, [dispatch, params.complaintMessageId])


  const change = (newComplaintMessageInfo: ComplaintMessage) => {
    setComplaintMessageInfo(newComplaintMessageInfo)
    setChanged(newComplaintMessageInfo.message.trim() !== '')
  }

  const handleOnSubmit = (event: FormEvent) => {
    event.preventDefault()
    changeComplaint()
  }

  const handleOnKeydown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && changed) {
      event.preventDefault()
      event.stopPropagation()
      changeComplaint()
    }
  }

  const setAttachedFileInfo = async () => {
    const files = (fileInput as any).current.files as File[];
    if (files.length && files[0]) {
      setComplaintMessageInfo({
        ...complaintMessageInfo,
        message: complaintMessageInfo.message + (complaintMessageInfo.message ? ' / ' : '') + `Se envía correo electrónico con el archivo adjunto ${files[0].name}`
      })
      setChanged(true)
      setAttached(true)
    }
  }

  const handleAttachedFile = async () => {
    const files = (fileInput as any).current.files as File[];
    if (!files.length) 
      {
      setReadMessages(true)
      return;
      }
    if (files[0]) {
      const formData = new FormData();
      formData.append("file", files[0]);
      formData.append("fileName", files[0].name);
      dispatch(uploadComplaintMessageFile(formData, complaintMessageInfo.complaintId,
        (fileId: string) => {
          setComplaintMessageFileId(fileId);
          setComplaintMessageInfo({ ...complaintMessageInfo, fileId: fileId })
        }))
    }
  }


  const handleComplaintMessageId = (messageId: number) => setComplaintMessageId(messageId)

  const changeComplaint = async () => {
    setChanged(false)
    setFetchingUploadFile(true)
    await dispatch(upsertComplaintMessage({ ...complaintMessageInfo, date: moment().tz("America/Argentina/Buenos_Aires").format()}, handleComplaintMessageId))
  }

  useEffect(() => {
    if (complaintMessageId) handleAttachedFile()
// eslint-disable-next-line react-hooks/exhaustive-deps      
  }, [complaintMessageId])

  useEffect(() => {
    if (complaintMessageFileId) {
      dispatch(upsertComplaintMessage({ ...complaintMessageInfo, date: moment().tz("America/Argentina/Buenos_Aires").format(), fileId: complaintMessageFileId, id: complaintMessageId }, 
          () => setReadMessages(true)))
    }
// eslint-disable-next-line react-hooks/exhaustive-deps          
  }, [complaintMessageFileId, complaintMessageId, dispatch])

  useEffect(() => {
    if (readMessages) {
      setFetchingUploadFile(false)
      dispatch(fetchComplaintMessages(params.complaintId))
      setReadMessages(false)
    }
  }, [readMessages, params.complaintId, dispatch])



  useEffect(() => {
    if (message.message)
      setComplaintMessageInfo(params.complaintMessageId ? message.message : empty)
  }, [message.message, params.complaintMessageId, params.complaintId])


  useEffect(() => {
    if (params.complaintId)
      setComplaintMessageInfo({ ...complaintMessageInfo, complaintId: Number(params.complaintId) })
  }, [complaintMessageInfo, params.complaintId])

  const ComplaintMessageInfoField = useMemo(() => entityTextField(change), [])

  return (
    <Paper style={{ borderRadius: "10px", padding: "10px" }}>
      <form onSubmit={handleOnSubmit} onKeyDown={handleOnKeydown}>
        <Stack display="flex" flexDirection='row' justifyContent="space-between">
          <ComplaintMessageInfoField
            key="message"
            variant="outlined"
            label={t("message")}
            entity={complaintMessageInfo}
            attr="message"
            size="small"
            required
          />
          <Tooltip title={t('attachFile')}>
            <IconButton disabled={attached} size="small" sx={{ padding: "5px" }} onClick={(event: FormEvent) => { event.preventDefault(); event.stopPropagation(); setShowUploadFile(!showUploadFile) }}>
              <AttachFile sx={{ color: theme.typography.caption.color }} />
            </IconButton>
          </Tooltip>
          <TextField
            InputLabelProps={{ shrink: true }}
            inputProps={{ accept: "application/pdf" }}
            inputRef={fileInput}
            label={t('attachFile')}
            margin="normal"
            size='small'
            variant="outlined"
            onChange={() => setAttachedFileInfo()}
            type="file"
            sx={{ marginLeft: "15px", display: showUploadFile ? 'default' : 'none' }}
            fullWidth
            disabled={attached}
          />
          <ProgressButton state={message} disabled={!changed} style={{ margin: "8px 0px 0px 8px" }}>
            {t('send')}
          </ProgressButton>
          {fetchingUploadFile &&
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', margin: "4px" }}>
              <CircularProgress size={25} />
            </div>
          }
        </Stack>
      </form>
    </Paper>

  )
}

const mapStateToProps = (state: AppState) => ({
  message: state.complaintMessageDetail
})

export default connect<Props, {}, {}, AppState>(mapStateToProps, {})(ComplaintMessageUpsert)
